import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { useState } from 'react'
import { useCreateProject, useDeleteProject, useProjects, useUpdateProject } from '../api/hooks'
import type { ProjectCreateRequest, ProjectStatus, ProjectSummary } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'
import { ActiveChip, StatusChip } from '../components/StatusChip'

const STATUSES: ProjectStatus[] = [
  'PLANNED',
  'IN_PROGRESS',
  'ON_HOLD',
  'COMPLETED',
  'CLOSED',
  'CANCELLED',
]

export function ProjectsPage() {
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<ProjectStatus | ''>('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [dialogOpen, setDialogOpen] = useState(false)

  const [editing, setEditing] = useState<ProjectSummary | null>(null)

  const query = useProjects({ q: search || undefined, status, page, size })
  const deleteProject = useDeleteProject()

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Every site, drawing and daily report hangs off a project."
        actions={
          can('PROJECT:CREATE') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              New project
            </Button>
          )
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search name or code"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ProjectStatus | '')
            setPage(0)
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace('_', ' ')}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {query.isLoading && <LoadingBlock />}
      {query.error && <ErrorBlock error={query.error} />}
      {deleteProject.error && <ErrorBlock error={deleteProject.error} title="Could not retire the project" />}

      {query.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Planned finish</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.content.map((project) => (
                <TableRow key={project.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{project.code}</TableCell>
                  <TableCell>{project.name}</TableCell>
                  <TableCell>{formatDate(project.startDate)}</TableCell>
                  <TableCell>{formatDate(project.plannedCompletionDate)}</TableCell>
                  <TableCell>
                    <StatusChip status={project.status} />
                  </TableCell>
                  <TableCell>
                    <ActiveChip active={project.active} />
                  </TableCell>
                  <TableCell align="right">
                    {can('PROJECT:UPDATE') && (
                      <Tooltip title="Edit project">
                        <IconButton size="small" onClick={() => setEditing(project)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {can('PROJECT:DELETE') && project.active && (
                      <Tooltip title="Retire project">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Retire "${project.name}"? Its history stays readable, but it disappears from pickers.`,
                              )
                            ) {
                              deleteProject.mutate(project.id)
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {query.data.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No projects match these filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={query.data.page.totalElements}
            page={query.data.page.number}
            rowsPerPage={query.data.page.size}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <ProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <ProjectDialog
        open={Boolean(editing)}
        project={editing ?? undefined}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

/** One dialog for both: with a project it edits, without one it creates. */
function ProjectDialog({
  open,
  project,
  onClose,
}: {
  open: boolean
  project?: ProjectSummary
  onClose: () => void
}) {
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()
  const [form, setForm] = useState<ProjectCreateRequest>({
    code: '',
    name: '',
    status: 'PLANNED',
  })

  // Loading the row into the form when the dialog opens on a different project.
  const [loadedId, setLoadedId] = useState<string | null>(null)
  if (open && project && loadedId !== project.id) {
    setLoadedId(project.id)
    setForm({
      code: project.code,
      name: project.name,
      status: project.status,
      startDate: project.startDate ?? '',
      plannedCompletionDate: project.plannedCompletionDate ?? '',
    })
  }

  const saving = createProject.isPending || updateProject.isPending
  const error = project ? updateProject.error : createProject.error

  function update<K extends keyof ProjectCreateRequest>(key: K, value: ProjectCreateRequest[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    const body: ProjectCreateRequest = {
      ...form,
      code: form.code.trim(),
      name: form.name.trim(),
      contractValue: form.contractValue ? Number(form.contractValue) : null,
      startDate: form.startDate || null,
      plannedCompletionDate: form.plannedCompletionDate || null,
    }
    try {
      if (project) {
        await updateProject.mutateAsync({ id: project.id, body })
        setLoadedId(null)
      } else {
        await createProject.mutateAsync(body)
        setForm({ code: '', name: '', status: 'PLANNED' })
      }
      onClose()
    } catch {
      // The error is rendered from the mutation state below.
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{project ? `Edit ${project.code}` : 'New project'}</DialogTitle>
      <DialogContent dividers>
        {Boolean(error) && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock
              error={error}
              title={project ? 'Could not save the project' : 'Could not create the project'}
            />
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}>
          <TextField
            label="Code"
            required
            value={form.code}
            disabled={Boolean(project)}
            onChange={(e) => update('code', e.target.value)}
            helperText={
              project ? 'Set once and not editable' : 'Letters, digits, dot, dash, underscore'
            }
          />
          <TextField
            select
            label="Status"
            value={form.status ?? 'PLANNED'}
            onChange={(e) => update('status', e.target.value as ProjectStatus)}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace('_', ' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
          <TextField
            label="Contract value"
            type="number"
            value={form.contractValue ?? ''}
            onChange={(e) => update('contractValue', e.target.value === '' ? null : Number(e.target.value))}
          />
          <TextField
            label="Description"
            value={form.description ?? ''}
            onChange={(e) => update('description', e.target.value)}
          />
          <TextField
            label="Start date"
            type="date"
            value={form.startDate ?? ''}
            onChange={(e) => update('startDate', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Planned completion"
            type="date"
            value={form.plannedCompletionDate ?? ''}
            onChange={(e) => update('plannedCompletionDate', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.code.trim() || !form.name.trim()}
        >
          {saving ? 'Saving...' : project ? 'Save changes' : 'Create project'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function formatDate(value?: string): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}
